import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ExternalLink,
  FileText,
  Landmark,
  UserMinus,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Pill } from '../../../../components/ui';
import { POST_LABEL } from '../../chart';
import { govDate, hostOf, plural } from '../../format';
import {
  ActEffects,
  ENDING_LABEL,
  TimelineLevel,
  actMatches,
  matchesQuery,
  personTone,
  tierShown,
} from '../../timeline';
import type { ActKind, GovActView, SeatView, TenureView } from '../../types';

export const ACT_KIND: Record<ActKind, { label: string; icon: LucideIcon; colour: string }> = {
  government: { label: 'Government', icon: Landmark, colour: '#7c3aed' },
  appointments: { label: 'Appointments', icon: Users, colour: '#0284c7' },
  appointment: { label: 'Appointment', icon: UserPlus, colour: '#0d9488' },
  dismissal: { label: 'Dismissal', icon: UserMinus, colour: '#d97706' },
  other: { label: 'Act', icon: FileText, colour: '#737373' },
};

export const actName = (act: { reference: string | null; date: string }) =>
  act.reference ?? `Act of ${govDate(act.date)}`;

/** A name, with the part a search found picked out */
export const Marked: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const term = query.trim();
  if (!term) return <>{text}</>;
  const fold = (value: string) => value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const at = fold(text).indexOf(fold(term));
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded bg-amber-100 px-0.5 text-inherit">{text.slice(at, at + term.length)}</mark>
      {text.slice(at + term.length)}
    </>
  );
};

/** One person's line under an act: who, in what post, and what the act did */
const PersonLine: React.FC<{
  tenure: TenureView;
  index: number;
  note?: string;
  query: string;
  onSelect?: (index: number) => void;
}> = ({ tenure, index, note, query, onSelect }) => {
  const tone = personTone(tenure);
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect?.(index)}
        className="flex w-full min-w-0 items-start gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-neutral-50"
      >
        <span className="mt-[5px] h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: tone.edge }} />
        <span className="min-w-0 flex-1 text-[12.5px] leading-snug">
          <span className="text-ink">
            <Marked text={tenure.person} query={query} />
          </span>
          <span className="text-neutral-500">
            {' '}
            — <Marked text={tenure.title} query={query} />
          </span>
          {tenure.acting && <span className="text-neutral-400"> · acting</span>}
          {note && <span className="text-neutral-400"> · {note}</span>}
        </span>
      </button>
    </li>
  );
};

/** What an act did, and for whom — the card both the chronicle and the chart open */
export const ActCard: React.FC<{
  act: GovActView;
  effects: ActEffects | undefined;
  tenures: TenureView[];
  seatOf: Map<number, SeatView>;
  level: TimelineLevel;
  query?: string;
  open?: boolean;
  onToggle?: () => void;
  onSelectTenure?: (index: number) => void;
  bare?: boolean;
}> = ({ act, effects, tenures, seatOf, level, query = '', open = true, onToggle, onSelectTenure, bare }) => {
  const kind = ACT_KIND[act.kind] ?? ACT_KIND.other;
  const Icon = kind.icon;
  const shown = (index: number) => {
    const seat = seatOf.get(index);
    return !seat || tierShown(seat.tier, level);
  };
  const began = (effects?.began ?? []).filter(shown);
  const kept = (effects?.kept ?? []).filter(shown);
  const ended = (effects?.ended ?? []).filter(shown);
  const read = act.status === 'read';
  const counts = [
    began.length ? `${began.length} took office` : null,
    kept.length ? `${kept.length} kept` : null,
    ended.length ? `${ended.length} left` : null,
  ].filter(Boolean);

  const sections: Array<{ title: string; items: number[]; note?: (t: TenureView) => string | undefined }> = [
    { title: 'Took office', items: began, note: (t) => (t.openStart ? 'already there before' : undefined) },
    { title: 'Kept in post', items: kept },
    {
      title: 'Left',
      items: ended,
      note: (t) => (t.ended ? ENDING_LABEL[t.ended]?.toLowerCase() : undefined),
    },
  ];

  return (
    <div className={bare ? '' : 'rounded-2xl border border-neutral-200 bg-white'}>
      <button
        type="button"
        onClick={onToggle}
        disabled={!onToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors enabled:hover:bg-neutral-50/70 disabled:cursor-default"
      >
        <span
          className="mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${kind.colour}14`, color: kind.colour }}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11.5px] tabular-nums text-neutral-400">{govDate(act.date)}</span>
          <span className="block break-words text-[13px] leading-snug text-ink">
            <Marked text={act.title} query={query} />
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Pill tone="outline">{kind.label}</Pill>
            {act.complete && <Pill tone="violet">Whole government</Pill>}
            {act.endsAll && <Pill tone="amber">Ends the whole government</Pill>}
            {!read && <Pill tone={act.status === 'failed' ? 'red' : 'neutral'}>{act.status === 'failed' ? 'Could not be read' : 'To read'}</Pill>}
            {read && counts.length > 0 && <span className="text-[11.5px] text-neutral-500">{counts.join(' · ')}</span>}
            {act.reference && (
              <span className="break-all font-ui-mono text-[10.5px] text-neutral-400">{act.reference}</span>
            )}
          </span>
        </span>
        {onToggle && (read || act.sources.length > 0) && (
          <ChevronDown
            className={`mt-1 h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
            strokeWidth={1.75}
          />
        )}
      </button>
      {open && (read || act.sources.length > 0) && (
        <div className="space-y-3 border-t border-neutral-100 px-4 pb-3.5 pt-3">
          {sections
            .filter((section) => section.items.length)
            .map((section) => (
              <div key={section.title}>
                <p className="mb-1 text-[11px] uppercase tracking-[0.06em] text-neutral-400">
                  {section.title} <span className="normal-case tracking-normal">· {section.items.length}</span>
                </p>
                <ul className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                  {section.items.map((index) => (
                    <PersonLine
                      key={index}
                      index={index}
                      tenure={tenures[index]}
                      note={section.note?.(tenures[index])}
                      query={query}
                      onSelect={onSelectTenure}
                    />
                  ))}
                </ul>
              </div>
            ))}
          {read && !began.length && !kept.length && !ended.length && act.members.length > 0 && (
            <ul className="space-y-0.5">
              {act.members.map((member, index) => (
                <li key={`${member.name}-${index}`} className="text-[12.5px] text-neutral-600">
                  <span className="text-ink">{member.name}</span> — {member.title}
                </li>
              ))}
            </ul>
          )}
          {act.sources.length > 0 && (
            <ul className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5">
              {act.sources.map((source) => (
                <li key={source.url} className="min-w-0">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex max-w-full items-center gap-1 text-[12px] text-neutral-500 hover:text-ink"
                  >
                    <ExternalLink className="h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
                    <span className="truncate">{hostOf(source.url) ?? source.url}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The chronicle: every act on file down a spine, a year at a time, each
 * saying what it did — who took office, who was kept, who left.
 */
export const TimelineActs: React.FC<{
  acts: GovActView[];
  tenures: TenureView[];
  effects: Map<string, ActEffects>;
  seatOf: Map<number, SeatView>;
  level: TimelineLevel;
  query: string;
  selectedAct: string | null;
  onSelectTenure: (index: number) => void;
}> = ({ acts, tenures, effects, seatOf, level, query, selectedAct, onSelectTenure }) => {
  const [open, setOpen] = useState<Set<string>>(() => new Set(selectedAct ? [selectedAct] : []));
  const refs = useRef(new Map<string, HTMLElement>());

  // A search also finds the acts that name a person in a matching post
  const shown = useMemo(
    () =>
      [...acts]
        .filter(
          (act) =>
            actMatches(act, query) ||
            [...(effects.get(act.id)?.began ?? []), ...(effects.get(act.id)?.ended ?? [])].some((index) =>
              matchesQuery(query, tenures[index].person, tenures[index].body),
            ),
        )
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [acts, effects, query, tenures],
  );
  const years = useMemo(() => {
    const byYear = new Map<string, GovActView[]>();
    for (const act of shown) {
      const year = act.date.slice(0, 4);
      byYear.set(year, [...(byYear.get(year) ?? []), act]);
    }
    return Array.from(byYear.entries());
  }, [shown]);

  useEffect(() => {
    if (!selectedAct) return;
    setOpen((current) => new Set(current).add(selectedAct));
    const node = refs.current.get(selectedAct);
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selectedAct]);

  if (!shown.length) {
    return (
      <p className="rounded-2xl border border-neutral-200 bg-white px-4 py-8 text-center text-[13px] text-neutral-400">
        No act matches.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* ---- jump to a year ---- */}
      <nav
        aria-label="Years"
        className="sticky top-0 z-10 -mx-1 flex gap-1 overflow-x-auto bg-[#fafafa]/90 px-1 py-1.5 backdrop-blur-sm"
      >
        {years.map(([year, list]) => (
          <button
            key={year}
            type="button"
            onClick={() =>
              document.getElementById(`timeline-year-${year}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            className="inline-flex h-7 flex-shrink-0 items-center gap-1.5 rounded-full bg-white px-3 text-[12px] text-neutral-600 ring-1 ring-neutral-200 transition-colors hover:text-ink hover:ring-neutral-300"
          >
            {year}
            <span className="text-neutral-400">{list.length}</span>
          </button>
        ))}
      </nav>

      {years.map(([year, list]) => (
        <section key={year} id={`timeline-year-${year}`} className="scroll-mt-12">
          <h3 className="mb-2 flex items-baseline gap-2 px-1">
            <span className="text-[22px] tracking-[-0.02em] text-ink">{year}</span>
            <span className="text-[12px] text-neutral-400">{plural(list.length, 'act')}</span>
          </h3>
          <ol className="relative space-y-2.5 pl-5 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-px before:bg-neutral-200">
            {list.map((act) => {
              const kind = ACT_KIND[act.kind] ?? ACT_KIND.other;
              const isOpen = open.has(act.id);
              return (
                <li
                  key={act.id}
                  ref={(node) => {
                    if (node) refs.current.set(act.id, node);
                  }}
                  className="relative"
                >
                  <span
                    className={`absolute -left-5 top-[18px] h-[15px] w-[15px] rounded-full border-2 border-white ${
                      act.complete ? 'ring-2' : ''
                    }`}
                    style={{
                      backgroundColor: kind.colour,
                      boxShadow: act.complete ? `0 0 0 3px ${kind.colour}33` : undefined,
                    }}
                  />
                  <div className={selectedAct === act.id ? 'rounded-2xl ring-2 ring-primary-600/50' : ''}>
                    <ActCard
                      act={act}
                      effects={effects.get(act.id)}
                      tenures={tenures}
                      seatOf={seatOf}
                      level={level}
                      query={query}
                      open={isOpen}
                      onToggle={() =>
                        setOpen((current) => {
                          const next = new Set(current);
                          if (next.has(act.id)) next.delete(act.id);
                          else next.add(act.id);
                          return next;
                        })
                      }
                      onSelectTenure={onSelectTenure}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
};

export const postLabel = (post: string) => POST_LABEL[post] ?? post.replace(/_/g, ' ');
